import chatChannelProvider from '../../chats/chatChannelProvider';
import { AppThunkAction } from '../index';
import { ChatConversationId, ChatMessage } from '../../types/chatMessage';
import { createChatChannelsAction } from './index';
import { selectIsUnreadMessageId } from '../../reducers/chats/messages';
import { toConversationId } from './conversationIdentity';
import { selectIsCurrentChatMessageConversation } from '../../reducers/chats/messages';
import { SupportedChatChannel } from '../../chats/supportedChatChannel';

const setUnreadMessagesIdsAction = createChatChannelsAction<{
  conversationId: ChatConversationId;
  unreadMessagesIds: string[];
}>('messages/setUnreadMessagesIds');

const addUnreadMessageIdAction = createChatChannelsAction<{
  conversationId: ChatConversationId;
  msgId: string;
}>('messages/addUnreadMessageId');

const removeUnreadMessageIdAction = createChatChannelsAction<{
  conversationId: ChatConversationId;
  msgId: string;
}>('messages/removeUnreadMessageId');

export const clearUnreadMessagesByConversation = (conversationId: ChatConversationId): AppThunkAction => {
  return (dispatch) => {
    const normalizedConversationId: ChatConversationId = {
      ...conversationId,
      senderId: String(conversationId.senderId),
      clientId: String(conversationId.clientId),
    };
    dispatch(
      setUnreadMessagesIdsAction(normalizedConversationId.channel, {
        conversationId: normalizedConversationId,
        unreadMessagesIds: [],
      })
    );
  };
};

export const clearUnreadMessagesByClient = (params: {
  channel: SupportedChatChannel;
  clientId: string;
}): AppThunkAction => {
  return (dispatch, getState) => {
    const { channel, clientId } = params;
    const normalizedClientId = String(clientId);
    const channelConversations = getState().chatChannels[channel].messages;

    channelConversations
      .filter((conversation) => String(conversation.clientId) === normalizedClientId && conversation.unreadMessagesIds.length)
      .forEach((conversation) => {
        dispatch(
          clearUnreadMessagesByConversation({
            channel,
            senderId: String(conversation.senderId),
            clientId: String(conversation.clientId),
          })
        );
      });
  };
};

const fetchUnreadMessagesIdsByConversationId = (conversationId: ChatConversationId): AppThunkAction => {
  return async (dispatch, getState) => {
    const { channel} = conversationId;
    try {
      const unreadMessagesIds: string[] = await chatChannelProvider.get(channel)
        .api.fetchUnreadMessagesIds(conversationId);

      const isCurrentConversation = selectIsCurrentChatMessageConversation(getState(), conversationId);

      dispatch(setUnreadMessagesIdsAction(channel, {
        conversationId,
        unreadMessagesIds: isCurrentConversation ? [] : unreadMessagesIds,
      }));
    } catch {
      /* ignore unread sync errors */
    }
  };
};

export const fetchUnreadMessagesForAllChannels = (): AppThunkAction => {
  return (dispatch, getState) => {
    const { customerUuid } = getState().login.userData;
    const channels: SupportedChatChannel[] = [
      SupportedChatChannel.SMS,
      SupportedChatChannel.WHATSAPP,
      SupportedChatChannel.VIBER,
    ];

    channels.forEach((channel) => {
      void (async () => {
        try {
          const unreadConversationIds = await chatChannelProvider.get(channel).api.fetchUnreadConversationsIds(customerUuid!);
          const normalizedUnreadConversationIds = unreadConversationIds.map((conversationId) => ({
            ...conversationId,
            senderId: String(conversationId.senderId),
            clientId: String(conversationId.clientId),
          }));
          const unreadConversationKeys = new Set(
            normalizedUnreadConversationIds.map((conversationId) => `${conversationId.senderId}::${conversationId.clientId}`)
          );

          // Keep local unread state in sync with backend snapshot and clear stale conversations
          // that are no longer unread on backend.
          getState()
            .chatChannels[channel]
            .messages
            .forEach((conversation) => {
              const key = `${String(conversation.senderId)}::${String(conversation.clientId)}`;
              if (!unreadConversationKeys.has(key) && conversation.unreadMessagesIds.length) {
                dispatch(
                  clearUnreadMessagesByConversation({
                    channel,
                    senderId: String(conversation.senderId),
                    clientId: String(conversation.clientId),
                  })
                );
              }
            });

          normalizedUnreadConversationIds.forEach((conversationId) => {
            dispatch(fetchUnreadMessagesIdsByConversationId(conversationId));
          });
        } catch {
          
        }
      })();
    });
  };
};

export const markMessageAsRead = (chatMessage: ChatMessage, force = false): AppThunkAction => {
  return async (dispatch, getState) => {
    const { channel, msgId } = chatMessage;
    const conversationId: ChatConversationId = toConversationId(chatMessage);

    const isUnreadMessage = selectIsUnreadMessageId(getState(), conversationId, msgId);
    if (force || isUnreadMessage) {
      try {
        await chatChannelProvider.get(channel).api.markMessageAsRead(chatMessage);
        dispatch(removeUnreadMessageIdAction(channel, { conversationId, msgId }));
      } catch {
        
      }
    }
  };
};

export const markMessageAsUnread = (chatMessage: ChatMessage): AppThunkAction => {
  return (dispatch, getState) => {
    const { channel, msgId } = chatMessage;
    const conversationId: ChatConversationId = toConversationId(chatMessage);

    const isUnreadMessage = selectIsUnreadMessageId(getState(), conversationId, msgId);
    if (!isUnreadMessage) {
      dispatch(addUnreadMessageIdAction(channel, { conversationId, msgId }));
    }
  };
};
